'use client';

import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useSSE } from 'shared/hooks/useSSE';
import { dataBackupStatusHandler } from '@/enteties/file-storage';
import { postgresBackupsHandler } from '@/enteties/postgresql';
import { buildStartHandler, instanceChangeHandler, repositoryHandler, serviceRunStatusHandler, taskHandler } from '@/enteties/project';
import { EventContext } from 'widgets/persistant-snackbars/context/event-context';
import type { UserErrorLogs } from '@/enteties/broadcast/model';
import dynamic from 'next/dynamic';
const LogSnackbarHandler = dynamic(() => import('widgets/persistant-snackbars/ui/persistant-snackbars'), {
  ssr: false
});
export default function EventProcessor({
  children
}: {
  children: ReactNode;
}) {
  const [userLogsErrors, setUserLogsErrors] = useState<UserErrorLogs | null>(null);
  const queryClient = useQueryClient();
  const handlers: Record<string, (data: any, queryClient: any) => void> = useMemo(() => ({
    repository: repositoryHandler,
    task: taskHandler,
    'service-run-status': serviceRunStatusHandler,
    'instances-change': instanceChangeHandler,
    'build-start': buildStartHandler,
    'postgres-backup-status': postgresBackupsHandler,
    'data-backup-status': dataBackupStatusHandler,
    'broadcast-message': data => queryClient.setQueryData(['active-message'], () => {
      return data?.payload;
    }),
    'user-logs-errors': data => setUserLogsErrors(data?.payload)
    // add other handlers here
  }), [queryClient]);
  const onMessage = useCallback((data: any) => {
    const handler = handlers[data.type];
    if (handler) {
      handler(data, queryClient);
    }
  }, [handlers, queryClient]);
  const sse = useSSE('events', onMessage);
  const contextValue = useMemo(() => ({
    userLogsErrors
  }), [userLogsErrors]);
  return <EventContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="EventProcessor" data-sentry-source-file="event-processor.tsx">
      {children}
      <LogSnackbarHandler data-sentry-element="LogSnackbarHandler" data-sentry-source-file="event-processor.tsx" />
    </EventContext.Provider>;
}